import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Banner shown to authenticated local-auth users whose password predates the
 * strength policy (min 8 chars, upper/lower/special). Renders once per session.
 * Users can dismiss it, but it re-appears on next session until they update.
 */
export default function WeakPasswordBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("qat_weak_pw_dismissed") === "1") {
        setDismissed(true);
      }
    } catch (_) {}
  }, []);

  if (!user) return null;
  if (user.auth_provider && user.auth_provider !== "local") return null;
  if (user.password_meets_policy) return null;
  if (dismissed) return null;

  const profilePath =
    user.role === "employer" ? "/employer/profile" :
    user.role === "job_seeker" ? "/profile" :
    "/admin";

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem("qat_weak_pw_dismissed", "1"); } catch (_) {}
  };

  return (
    <div
      className="mb-4 p-3 rounded-md border border-amber-200 bg-amber-50 flex items-start gap-3"
      data-testid="weak-password-banner"
      role="alert"
    >
      <span className="h-8 w-8 rounded-md bg-white grid place-items-center text-amber-700 flex-shrink-0 border border-amber-200">
        <ShieldAlert className="h-4 w-4" />
      </span>
      <div className="flex-1 text-sm text-zinc-800">
        <div className="font-semibold text-zinc-950">Your password looks weak</div>
        <p className="text-zinc-700 mt-0.5">
          We updated our security policy. Please update your password so it has at least
          8 characters with a mix of upper, lower and special characters.{" "}
          <Link
            to={profilePath}
            className="underline font-medium text-[#002FA7] hover:text-[#00227A]"
            data-testid="weak-password-update-link"
          >
            Update now →
          </Link>
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="weak-password-dismiss"
        className="text-zinc-500 hover:text-zinc-900 -mt-0.5"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
